/**
 * Field types and API client.
 *
 * This file contains only type definitions and the API client.
 */

import type { Feature, FeatureCollection } from "geojson";

type Visibility = "none" | "visible";

export type FieldResponse = {
  guid: string;
  name: string;
  farm: string;
  geom: FeatureCollection;
  area: number;
  tiles?: Array<string>;
  dryMatterNow: number;
  animalGroup?: string;
  animalGroupNow?: string;
};

export type Field = {
  guid: string;
  name: string;
  farm: string;
  geom: Feature;
  area: number;
  invalidatedTimestamp?: number;
  tiles?: Array<string>;
  visibility?: Visibility;
  dryMatterNow: number;
  animalGroup?: string;
  animalGroupNow?: string;
  grassType?: GRASS_TYPE;
};

export type FieldUpdate = {
  guid: string;
  name?: string;
  farm?: string;
  geom?: Feature;
  area?: number;
  invalidatedTimestamp?: number;
  visibility?: Visibility;
  dryMatterNow?: number;
  animalGroup?: string;
  grassType?: GRASS_TYPE;
};

export enum GRASS_TYPE {
  PERMANENT_PASTURE = "PERMANENT_PASTURE",
  HERBAL_LEY = "HERBAL_LEY",
  RYE_GRASS = "RYE_GRASS",
  RYE_GRASS_AND_CLOVER = "RYE_GRASS_AND_CLOVER",
  WILDFLOWER_MEADOW = "WILDFLOWER_MEADOW",
  LUCERNE_SAINFOIN = "LUCERNE_SAINFOIN",
  BRASSICAS = "BRASSICAS",
  OTHER = "OTHER",
}

/**
 * Get color for a dry matter value.
 * Used for field coloring on the map.
 */
export const getDryMatterColor = (dryMatter: number | undefined): string => {
  if (dryMatter === undefined) return "#2e3440";
  if (dryMatter < 0.07) return "#A8917B";
  if (dryMatter < 0.1) return "#CABFA1";
  if (dryMatter < 0.115) return "#D6D6AD";
  if (dryMatter < 0.13) return "#D7E7AA";
  if (dryMatter < 0.145) return "#D1F094";
  if (dryMatter < 0.16) return "#C5E97C";
  if (dryMatter < 0.175) return "#B3E579";
  if (dryMatter < 0.19) return "#A5DE77";
  if (dryMatter < 0.205) return "#99D775";
  if (dryMatter < 0.22) return "#8ED173";
  if (dryMatter < 0.235) return "#82CA71";
  if (dryMatter < 0.25) return "#76C36F";
  if (dryMatter < 0.265) return "#6ABC6D";
  if (dryMatter < 0.28) return "#62B36C";
  if (dryMatter < 0.295) return "#59AB6C";
  if (dryMatter < 0.31) return "#52A36B";
  if (dryMatter < 0.34) return "#4A9A6A";
  if (dryMatter < 0.37) return "#41916A";
  if (dryMatter < 0.4) return "#388969";
  if (dryMatter < 0.45) return "#318168";
  if (dryMatter < 0.5) return "#1E7662";
  if (dryMatter < 0.6001) return "#0E6E5C";
  return "#2e3440";
};
